import { Button } from '@connected/ui/components/button';
import {
    Carousel,
    CarouselContent,
    CarouselIndicators,
    CarouselItem,
} from '@connected/ui/components/carousel';
import { cn } from '@connected/ui/lib/utils';
import { useTranslation } from 'react-i18next';

export type ModuleHeroProps = {
    moduleType?: string;
};

export function ModuleHero({ moduleType }: ModuleHeroProps) {
    const { t } = useTranslation('customer-module-preview');

    const previewImages = import.meta.glob('/src/assets/previews/*.webp', { eager: true, import: 'default' });
    const availableImages = [
        `/src/assets/previews/${moduleType}-preview.webp`,
        `/src/assets/previews/${moduleType}-preview-2.webp`,
        `/src/assets/previews/${moduleType}-preview-3.webp`,
    ].filter(path => previewImages[path]).map(path => String(previewImages[path]));

    return (
        <div className="flex flex-col-reverse items-center w-auto justify-between gap-8 lg:flex-row">
            <div className={cn('flex flex-col items-start justify-center gap-6 text-center', { 'w-full': availableImages.length < 1 })}>
                <h1 className={cn('text-5xl font-bold text-center', { 'w-full': availableImages.length < 1 })}>
                    {t(`module-preview.${moduleType}.title`)}
                </h1>
                <div className={cn('', { 'flex justify-center w-full': availableImages.length < 1 })}>
                    <p
                        className={cn('text-xl text-text-primary/70 gap-2', {
                            'w-[24rem] text-left': availableImages.length > 0,
                            'w-[48rem] text-center': availableImages.length < 1,
                        })}
                    >
                        {t(`module-preview.${moduleType}.text`)}
                    </p>
                </div>
                <div className={cn('', { 'flex justify-center w-full': availableImages.length < 1 })}>
                    <Button size="lg" className="text-lg p-5">
                        {t('module-preview.button')}
                    </Button>
                </div>
            </div>
            {availableImages.length > 0 && (
                <div className="w-2/3 flex items-center justify-center border-2 rounded-2xl">
                    <div className="m-0.5 border-4 border-background-tertiary/70 rounded-2xl overflow-hidden">
                        <Carousel loop>
                            {availableImages.length > 1
                                ? (
                                        <>
                                            <CarouselContent className="-ml-2 md:-ml-4">
                                                {availableImages.map((imageSrc: string) => (
                                                    <CarouselItem key={imageSrc} className="pl-2 md:pl-4">
                                                        <img
                                                            src={imageSrc}
                                                            alt={`${moduleType} preview`}
                                                        />
                                                    </CarouselItem>
                                                ))}
                                            </CarouselContent>
                                            <CarouselIndicators />
                                        </>
                                    )
                                : (
                                        <img
                                            src={availableImages[0]}
                                            alt={`${moduleType} preview`}
                                        />
                                    )}
                        </Carousel>
                    </div>
                </div>
            )}
        </div>
    );
};
